import { listAllPanelInstances } from "./traverse";
import type { AddTarget, Layout, PanelInstance, TabGroup } from "./types";

/**
 * Helpers mobile : aplatissement du tree desktop en un seul TabGroup, et
 * coercion des AddTarget desktop (split-edge) vers une cible compatible
 * mono-panel (active-group).
 *
 * Philosophie : le STATE desktop reste intact (Splits + sizes preserves).
 * On change uniquement ce que le shell RENDU en mode mobile, et on intercepte
 * les mutations qui creeraient des Splits pour les rediriger vers le
 * TabGroup actif. Quand l'utilisateur revient en desktop (autre device,
 * meme workspace synced), il retrouve son layout original.
 */

const MOBILE_GROUP_ID = "__mobile_root__";

/**
 * Aplatit un Layout desktop en un TabGroup unique contenant tous les panels
 * en ordre DFS. Le `activeIndex` est preserve si possible (lookup de
 * l'ancien instance actif dans le focused group, sinon defaut 0).
 *
 * @param focusedInstanceId — ID d'une instance active a privilegier pour
 *   le activeIndex (optionnel). Typiquement celle du focused TabGroup
 *   desktop, ou la derniere instance ajoutee en mode mobile.
 */
export function flattenLayoutForMobile(
  layout: Layout,
  focusedInstanceId?: string | null,
): TabGroup {
  const panels = listAllPanelInstances(layout);
  let activeIndex = -1;

  if (panels.length > 0) {
    if (focusedInstanceId) {
      const idx = panels.findIndex((p) => p.id === focusedInstanceId);
      activeIndex = idx >= 0 ? idx : 0;
    } else {
      activeIndex = 0;
    }
  }

  return {
    kind: "tabs",
    id: MOBILE_GROUP_ID,
    panels,
    activeIndex,
  };
}

/** ID stable du TabGroup racine virtuel en mode mobile. */
export function mobileRootGroupId(): string {
  return MOBILE_GROUP_ID;
}

/**
 * Coerce un AddTarget desktop en un equivalent mobile-safe.
 *
 * Strategie :
 *   - `split-edge` → `active-group` (l'utilisateur s'attend a un nouvel onglet,
 *     pas un nouveau panneau cote a cote)
 *   - `group` reste tel quel SI le groupId est le TabGroup mobile virtuel
 *     OU un vrai TabGroup desktop (les mutations addPanel l'ajoutent au
 *     tree desktop, qui sera replatti au prochain render mobile)
 *   - `active-group` passe inchange
 *
 * NB : ne PAS chercher si le groupId existe vraiment dans le layout — la
 * coercition se fait avant `addPanel`, qui validera plus loin. Coercer en
 * silence un `group` desconnu vers `active-group` masquerait des bugs.
 */
export function coerceMobileAddTarget(target: AddTarget): AddTarget {
  if (target.kind === "split-edge") {
    return { kind: "active-group" };
  }
  return target;
}

/**
 * En mode mobile, l'instance active est celle visible au top. On l'extrait
 * du TabGroup flatten.
 */
export function activeMobileInstance(
  layout: Layout,
  focusedInstanceId?: string | null,
): PanelInstance | null {
  const flat = flattenLayoutForMobile(layout, focusedInstanceId);
  if (flat.activeIndex < 0 || flat.activeIndex >= flat.panels.length) {
    return null;
  }
  return flat.panels[flat.activeIndex] ?? null;
}
